import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@clerk/react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2 } from "lucide-react";

export type CustomFieldDefinition = {
  id: number;
  entityType: "contract" | "coach";
  key: string;
  label: string;
  type: "text" | "long_text" | "number" | "currency" | "date" | "dropdown" | "checkbox";
  options: string[];
  required: boolean;
  helpText: string | null;
  displayOrder: number;
  enabled: boolean;
};

export type CustomFieldEntityValue = {
  definition: CustomFieldDefinition;
  value: unknown;
  valueId: number | null;
};

interface CustomFieldsSectionProps {
  entityType: "contract" | "coach";
  entityId?: number;
  readOnly?: boolean;
  values?: Record<string, unknown>;
  onChange?: (key: string, value: unknown) => void;
  errors?: Record<string, string>;
}

export function CustomFieldsSection({
  entityType,
  entityId,
  readOnly = false,
  values: externalValues,
  onChange,
  errors,
}: CustomFieldsSectionProps) {
  const { getToken } = useAuth();
  const [fields, setFields] = useState<CustomFieldEntityValue[]>([]);
  const [loading, setLoading] = useState(false);
  const [localValues, setLocalValues] = useState<Record<string, unknown>>({});

  const loadFields = useCallback(async () => {
    try {
      setLoading(true);
      const token = await getToken();
      const params = new URLSearchParams({ entityType });
      if (entityId != null) params.set("entityId", String(entityId));
      else params.set("entityId", "0");
      const res = await fetch(`/api/custom-fields/values?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return;
      const data = (await res.json()) as CustomFieldEntityValue[];
      const enabled = data.filter((d) => d.definition.enabled);
      setFields(enabled);
      if (!externalValues) {
        const vals: Record<string, unknown> = {};
        for (const f of enabled) {
          if (f.value !== null) vals[f.definition.key] = f.value;
        }
        setLocalValues(vals);
      }
    } catch {
      // silently fail
    } finally {
      setLoading(false);
    }
  }, [entityType, entityId, getToken, externalValues]);

  useEffect(() => {
    loadFields();
  }, [loadFields]);

  const values = externalValues ?? localValues;

  const handleChange = (key: string, value: unknown) => {
    if (onChange) {
      onChange(key, value);
    } else {
      setLocalValues((prev) => ({ ...prev, [key]: value }));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading custom fields…
      </div>
    );
  }

  if (fields.length === 0) return null;

  return (
    <div className="space-y-4">
      <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">Custom Fields</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {fields.map(({ definition: def, value: savedValue }) => {
          const currentValue = def.key in values ? values[def.key] : savedValue;
          const error = errors?.[def.key];

          if (readOnly) {
            return (
              <div key={def.id} className="space-y-1">
                <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{def.label}</span>
                <div className="text-sm">
                  <CustomFieldReadValue type={def.type} value={currentValue} />
                </div>
              </div>
            );
          }

          return (
            <div key={def.id} className={`space-y-1 ${def.type === "long_text" ? "md:col-span-2" : ""}`}>
              <Label htmlFor={`cf-${def.key}`} className="text-sm">
                {def.label}
                {def.required && <span className="text-destructive ml-0.5">*</span>}
              </Label>
              {def.helpText && (
                <p className="text-xs text-muted-foreground">{def.helpText}</p>
              )}
              <CustomFieldInput
                def={def}
                value={currentValue}
                onChange={(v) => handleChange(def.key, v)}
              />
              {error && <p className="text-xs text-destructive">{error}</p>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function CustomFieldReadValue({ type, value }: { type: string; value: unknown }) {
  if (value === null || value === undefined || value === "") {
    return <span className="text-muted-foreground italic">—</span>;
  }
  if (type === "checkbox") {
    return <span>{value ? "Yes" : "No"}</span>;
  }
  if (type === "currency") {
    const n = typeof value === "number" ? value : parseFloat(String(value));
    return <span>{isNaN(n) ? String(value) : `$${n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}</span>;
  }
  if (type === "number") {
    const n = typeof value === "number" ? value : parseFloat(String(value));
    return <span>{isNaN(n) ? String(value) : n.toLocaleString("en-US")}</span>;
  }
  return <span>{String(value)}</span>;
}

function CustomFieldInput({
  def,
  value,
  onChange,
}: {
  def: CustomFieldDefinition;
  value: unknown;
  onChange: (v: unknown) => void;
}) {
  const strValue = value === null || value === undefined ? "" : String(value);

  switch (def.type) {
    case "text":
      return (
        <Input
          id={`cf-${def.key}`}
          value={strValue}
          onChange={(e) => onChange(e.target.value)}
          placeholder={def.label}
        />
      );
    case "long_text":
      return (
        <Textarea
          id={`cf-${def.key}`}
          value={strValue}
          onChange={(e) => onChange(e.target.value)}
          placeholder={def.label}
          rows={3}
        />
      );
    case "number":
    case "currency":
      return (
        <Input
          id={`cf-${def.key}`}
          type="number"
          value={strValue}
          onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))}
          placeholder={def.type === "currency" ? "0.00" : "0"}
          step={def.type === "currency" ? "0.01" : "1"}
        />
      );
    case "date":
      return (
        <Input
          id={`cf-${def.key}`}
          type="date"
          value={strValue}
          onChange={(e) => onChange(e.target.value || null)}
        />
      );
    case "dropdown":
      return (
        <Select value={strValue || undefined} onValueChange={(v) => onChange(v)}>
          <SelectTrigger id={`cf-${def.key}`}>
            <SelectValue placeholder={`Select ${def.label}`} />
          </SelectTrigger>
          <SelectContent>
            {def.options.map((opt) => (
              <SelectItem key={opt} value={opt}>{opt}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      );
    case "checkbox":
      return (
        <div className="flex items-center gap-2 py-1">
          <Checkbox
            id={`cf-${def.key}`}
            checked={value === true || value === "true" || value === 1}
            onCheckedChange={(checked) => onChange(!!checked)}
          />
          <label htmlFor={`cf-${def.key}`} className="text-sm cursor-pointer">
            {def.label}
          </label>
        </div>
      );
    default:
      return (
        <Input
          id={`cf-${def.key}`}
          value={strValue}
          onChange={(e) => onChange(e.target.value)}
        />
      );
  }
}

export async function saveCustomFieldValues(
  entityType: "contract" | "coach",
  entityId: number,
  values: Record<string, unknown>,
  token: string,
): Promise<{ ok: boolean; fieldErrors?: Record<string, string> }> {
  try {
    const res = await fetch("/api/custom-fields/values", {
      method: "PUT",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ entityType, entityId, values }),
    });
    if (res.status === 422) {
      const data = (await res.json()) as { fieldErrors?: Record<string, string> };
      return { ok: false, fieldErrors: data.fieldErrors };
    }
    if (!res.ok) return { ok: false };
    return { ok: true };
  } catch {
    return { ok: false };
  }
}
